import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { RosterWeekly } from './roster-weekly/roster-weekly';
import RosterDaily from './roster-daily/roster-daily';
import { RosterLoadingIndicator } from 'views/bookings-v2/bookings-roster-view/components/RosterLoadingIndicator';
import { LoaderElement } from 'views/bookings-v2/bookings-roster-view/components/LoaderElement';
import { ROSTER_DISPLAY_MODE, ROSTER_TAB } from 'views/bookings-v2/bookings-roster-view/components/roster-common-enums';
import { useQueryInfiniteShifts } from 'stores/hooks/query-hooks/use-query-infinite-shifts';
import { useQueryInfiniteBookings } from 'stores/hooks/query-hooks/use-query-infinite-bookings';
import { flattenPages } from 'stores/hooks/common/common-hook-utils';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { useBookings } from '../books-view-context/bookings-view-context';
import { Box, Center, Container, ScrollArea, Stack, Text } from '@good/ui/core';
import { FilterType } from 'utilities/enum-utils';
import CommonUtils from 'utilities/common-utils';
import { HandsTogetherHeavier } from '@good/illustrations';
import { useViewportSize } from '@good/ui/hooks';
import { OnAddBooking } from './roster-daily/components/add-booking-popover';

import './styles.css';

export const BookingsRosterView = () => {
  const onChangeFilter = useBookings((selector) => selector.onChangeFilter);
  const onOpenCreateBooking = useBookings((selector) => selector.onOpenCreateBooking);
  const selectedMode = useSelector((state: IRootState) => state.bookingsStore.rosterMode);
  const { bookingsFilter } = useSelector((state: IRootState) => state.bookingsStore);
  const { navigationStore: navigationDispatch, customersStore: customersDispatch } = useDispatch<IRootDispatch>();
  const { setSelectedSideNavMenuKeys } = navigationDispatch;
  const { doGetCustomer } = customersDispatch;
  const { height } = useViewportSize();
  const registerRefetchRoster = useBookings((selector) => selector.registerRefetchRoster);
  const h = height - 60;

  const queryDateFilter = useMemo(() => {
    let dateFilter = bookingsFilter.find((filter) => filter.filter === FilterType.DATE_RANGE);

    const dateFilterNonStandard = bookingsFilter.find((filter) => 'startDate' in filter);
    if (dateFilterNonStandard) {
      dateFilter = {
        filter: FilterType.DATE_RANGE,
        values: dateFilterNonStandard.startDate as [Date, Date],
      };
    }

    if (dateFilter) {
      return {
        filter: 'startDate',
        values: dateFilter.values,
        selectionLabel: `${moment(dateFilter.values[0]).format('dddd, DD MMM YYYY')} - ${moment(
          dateFilter.values[1],
        ).format('dddd, DD MMM YYYY')}`,
      };
    }

    return {
      filter: 'startDate',
      values: [],
      selectionLabel: 'All',
    };
  }, [bookingsFilter]);

  const selectedTab = moment(queryDateFilter.values[0]).isSame(queryDateFilter.values[1], 'day')
    ? ROSTER_TAB.DAILY
    : ROSTER_TAB.WEEKLY;

  const rangeLength = moment(queryDateFilter.values[1]).diff(moment(queryDateFilter.values[0]), 'days') + 1;
  const isAllRange = queryDateFilter.values.length === 0;
  const selectedDate = queryDateFilter.values[0];
  const selectedWeek: [Date, Date] = useMemo(
    () => [queryDateFilter.values[0], queryDateFilter.values[1]],
    [queryDateFilter],
  );

  // Check if user has filtered for specific shift statuses
  const shiftStatusFilter = bookingsFilter.find((filter) => filter.filter === 'shiftSlotStatus');
  const userShiftStatuses = shiftStatusFilter?.values || [];

  // Only fetch unassigned shifts if user hasn't filtered for specific statuses or if user's filter specifically includes UNASSIGNED
  const shouldFetchUnassignedShifts = userShiftStatuses.length === 0 || userShiftStatuses.includes('UNASSIGNED');

  const {
    data: unassignedShiftsData,
    fetchNextPage: fetchNextUnassignedShifts,
    hasNextPage: hasNextUnassignedShifts,
    isFetching: isFetchingNextUnassignedShifts,
    refetch: refetchUnassignedShifts,
  } = useQueryInfiniteShifts({
    fetchUnassignedShifts: true,
    filters: bookingsFilter,
    showOnlyAssigned: false,
    enabled: shouldFetchUnassignedShifts,
  });

  // hasNextPage by default is initialised as undefined on page load. Defer loading for assigned shifts until unassigned shifts query is resolved (if enabled)
  const shouldFetchShifts = shouldFetchUnassignedShifts
    ? hasNextUnassignedShifts === undefined
      ? false
      : !hasNextUnassignedShifts
    : true; // If unassigned query is disabled, always fetch assigned shifts

  const {
    data: shiftsData, // assigned shifts only
    fetchNextPage: fetchNextShifts,
    hasNextPage: hasNextShifts,
    isFetching: isFetchingNextShifts,
    refetch: refetchShifts,
  } = useQueryInfiniteShifts({
    filters: bookingsFilter,
    showOnlyAssigned: true,
    enabled: shouldFetchShifts,
  });

  // bookings
  const {
    data: bookingsData,
    fetchNextPage: fetchNextBookings,
    hasNextPage: hasNextBookings,
    isFetching: isFetchingNextBookings,
    refetch: refetchBookings,
  } = useQueryInfiniteBookings({ filters: bookingsFilter, enabled: true });

  const assignedShifts = useMemo(() => flattenPages(shiftsData), [shiftsData]);
  const unassignedShifts = useMemo(() => flattenPages(unassignedShiftsData), [unassignedShiftsData]);
  const bookings = useMemo(() => flattenPages(bookingsData), [bookingsData]);
  const shifts = [...(unassignedShifts || []), ...(assignedShifts || [])];

  // Has more rows to fetch (depending on which mode is selected)
  const getGeneralStateFromRosterMode = (nextUnassignedShifts: boolean, nextShifts: boolean, nextBookings: boolean) => {
    switch (selectedMode) {
      // If we're checking for everything, return true if there's ANYTHING needed.
      case ROSTER_DISPLAY_MODE.ALL:
        return nextUnassignedShifts || nextShifts || nextBookings;
      // Otherwise, only return values relevant to the roster display mode.
      case ROSTER_DISPLAY_MODE.TEAM_MEMBERS:
        return nextUnassignedShifts || nextShifts;
      case ROSTER_DISPLAY_MODE.CUSTOMERS:
        return nextBookings;
      default:
        return false;
    }
  };

  // If we have any more relevant data available
  const hasMore = getGeneralStateFromRosterMode(hasNextUnassignedShifts, hasNextShifts, hasNextBookings);

  // If there are any relevant queries running
  const isFetching = getGeneralStateFromRosterMode(
    isFetchingNextUnassignedShifts,
    isFetchingNextShifts,
    isFetchingNextBookings,
  );

  // Fetch more data when the user scrolls to the bottom of the list/request a reload / etc. Triggered by loadingElement.
  const fetchMore = async () => {
    if (
      selectedMode === ROSTER_DISPLAY_MODE.CUSTOMERS ||
      (selectedMode === ROSTER_DISPLAY_MODE.ALL && hasNextBookings)
    ) {
      return fetchNextBookings();
    }

    if (hasNextUnassignedShifts) {
      return fetchNextUnassignedShifts();
    }

    if (hasNextShifts) {
      return fetchNextShifts();
    }

    return;
  };

  const goToDailyDate = useCallback(
    (date: Date): void => {
      const next = bookingsFilter.filter((filter) => filter.filter !== FilterType.DATE_RANGE);
      onChangeFilter([
        {
          filter: FilterType.DATE_RANGE,
          values: [moment(date).startOf('day').toDate(), moment(date).endOf('day').toDate()],
          selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, [
            moment(date).startOf('day'),
            moment(date).endOf('day'),
          ]),
        },
        ...next,
      ]);
    },
    [bookingsFilter.filter, onChangeFilter],
  );

  const refreshData = useCallback(async () => {
    if (selectedMode !== ROSTER_DISPLAY_MODE.CUSTOMERS) {
      await refetchUnassignedShifts();
      await refetchShifts();
    }

    if (selectedMode !== ROSTER_DISPLAY_MODE.TEAM_MEMBERS) {
      await refetchBookings();
    }
  }, [refetchBookings, refetchShifts, refetchUnassignedShifts, selectedMode]);

  useEffect(() => {
    registerRefetchRoster(() => refreshData());
  }, [registerRefetchRoster, refreshData]);

  // Used to create booking within weekly cells.
  const createBookingForCustomer: OnAddBooking = async ({ customerId, startDateTime }) => {
    const selectedCustomer = await doGetCustomer({ userId: customerId });
    onOpenCreateBooking({ customer: selectedCustomer, startDate: startDateTime });
  };

  useEffect(() => {
    setSelectedSideNavMenuKeys(['/bookings?view=calendar']);
  }, [setSelectedSideNavMenuKeys]);

  if (rangeLength > 31 || isAllRange) {
    return (
      <Container my='xl'>
        <Center my='xl'>
          <Stack align='center'>
            <HandsTogetherHeavier size='lg' />
            <Text fw={550}>Please select a range of up to 31 days.</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <>
      <Box style={{ overflow: 'hidden', zIndex: 10, height: h }}>
        {isFetching && <RosterLoadingIndicator />}
        {selectedTab === 'daily' && (
          <ScrollArea h='100%'>
            <RosterDaily
              bookings={bookings}
              assignedShifts={assignedShifts}
              unassignedShifts={unassignedShifts}
              selectedDate={selectedDate}
              showCustomers={selectedMode === ROSTER_DISPLAY_MODE.CUSTOMERS || selectedMode === ROSTER_DISPLAY_MODE.ALL}
              showWorkers={
                selectedMode === ROSTER_DISPLAY_MODE.TEAM_MEMBERS ||
                (selectedMode === ROSTER_DISPLAY_MODE.ALL && !hasNextBookings) // Hide the team members section until bookings has completely loaded
              }
              onAddBooking={createBookingForCustomer}
              showOpenSlots={true}
              refreshData={refreshData}
            />
          </ScrollArea>
        )}
        {selectedTab === 'weekly' && (
          <RosterWeekly
            bookings={bookings}
            shifts={shifts}
            selectedWeek={selectedWeek}
            onClickHeaderDay={goToDailyDate}
            showCustomers={selectedMode === ROSTER_DISPLAY_MODE.CUSTOMERS || selectedMode === ROSTER_DISPLAY_MODE.ALL}
            showWorkers={
              selectedMode === ROSTER_DISPLAY_MODE.TEAM_MEMBERS ||
              (selectedMode === ROSTER_DISPLAY_MODE.ALL && !hasNextBookings) // Hide the team members section until bookings has completely loaded
            }
            isFetching={isFetching}
          />
        )}
        <LoaderElement isFetching={isFetching} onFetch={fetchMore} hasMore={hasMore} />
      </Box>
    </>
  );
};
